import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Download, Share, X, ArrowClockwise } from 'react-bootstrap-icons';
import { toast } from 'sonner';
import { readFile } from '../../helpers/api';
import { downloadFileFromBase64, humanFileSize } from '../../helpers/global';
import { bakePdfHighlights, normalizePdfHighlights } from '../../helpers/pdfHighlight';
import CircularProgressBar from './circular-progress';
import FileRenderer from './FileRenderer';
import { REPORT_GENERATION_MODES } from '../../helpers/constants';
import { IoIosWarning } from 'react-icons/io';

const FilePopup = ({
  file,
  mode,
  isGeneratingReport,
  onHide,
  refreshFile,
  isDownloadable = true,
  onShareClick,
  showQualiaShareButton = false,
  onQualiaShareClick,
  halfScreen = false,
  dock = 'left',
  containerStyle = {},
  enableAnnotations = false,
  initialHighlights = [],
  onHighlightsPersist
}) => {
  const [fileHref, setFileHref] = useState(file?.href);
  const [highlights, setHighlights] = useState(() => normalizePdfHighlights(initialHighlights));
  const [isBaking, setIsBaking] = useState(false);
  // Keep the original un-highlighted PDF source so annotations can be re-done.
  const originalHrefRef = useRef(file?.href);
  const bakedObjectUrlRef = useRef(null);
  const persistTimerRef = useRef(null);

  const isPdf = (file?.contentType || '').includes('pdf') || /\.pdf($|\?)/i.test(file?.filename || '');
  const canAnnotate = enableAnnotations && isPdf;

  const revokeBakedUrl = () => {
    if (bakedObjectUrlRef.current) {
      URL.revokeObjectURL(bakedObjectUrlRef.current);
      bakedObjectUrlRef.current = null;
    }
  };

  const getFileHREF = async () => {
    const { response, error } = await readFile({
      fileName: file.filename,
      mimeType: file.contentType
    });
    if (response) {
      setFileHref(response);
      originalHrefRef.current = response;
    } else {
      toast.error(error);
    }
  };

  useEffect(() => {
    setHighlights(normalizePdfHighlights(initialHighlights));
    revokeBakedUrl();
    if (file && !file?.href) {
      getFileHREF();
      return;
    }
    setFileHref(file?.href);
    originalHrefRef.current = file?.href;
    // Only re-seed when the previewed file changes; live edits persist via onHighlightsPersist.
  }, [file]);

  useEffect(() => {
    return () => {
      revokeBakedUrl();
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, []);

  const handleHighlightsChange = next => {
    const normalized = normalizePdfHighlights(next);
    setHighlights(normalized);
    if (!onHighlightsPersist) return;
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      onHighlightsPersist(normalized);
    }, 500);
  };

  const getDownloadFileName = () =>
    file.metadata && file.metadata.originalName ? file.metadata.originalName : file.filename;

  const getBakedPdfUrl = async () => {
    const source = originalHrefRef.current || fileHref;
    if (!source) return null;
    if (!highlights.length) return source;

    setIsBaking(true);
    try {
      const bakedBlob = await bakePdfHighlights(source, highlights);
      revokeBakedUrl();
      const bakedUrl = URL.createObjectURL(bakedBlob);
      bakedObjectUrlRef.current = bakedUrl;
      return bakedUrl;
    } catch (err) {
      console.error('Failed to bake PDF highlights', err);
      toast.error('Failed to apply highlights to PDF');
      return null;
    } finally {
      setIsBaking(false);
    }
  };

  const handleDownload = async () => {
    if (!fileHref) return;

    if (!canAnnotate || !highlights.length) {
      downloadFileFromBase64({
        data: fileHref,
        fileName: getDownloadFileName()
      });
      return;
    }

    const bakedUrl = await getBakedPdfUrl();
    if (!bakedUrl) return;
    downloadFileFromBase64({
      data: bakedUrl,
      fileName: getDownloadFileName()
    });
  };

  const handleShare = async () => {
    if (!onShareClick) return;
    if (!canAnnotate || !highlights.length) {
      onShareClick();
      return;
    }
    const bakedUrl = await getBakedPdfUrl();
    if (!bakedUrl) return;
    onShareClick(bakedUrl);
  };

  const handleQualiaShare = async () => {
    if (!onQualiaShareClick) return;
    if (!canAnnotate || !highlights.length) {
      onQualiaShareClick();
      return;
    }
    const bakedUrl = await getBakedPdfUrl();
    if (!bakedUrl) return;
    onQualiaShareClick(bakedUrl);
  };

  return (
    <div
      style={{
        height: '100%',
        width: halfScreen ? '50%' : '100%',
        position: 'fixed',
        zIndex: 20000,
        left: halfScreen ? (dock === 'left' ? 0 : '50%') : 0,
        top: 0,
        ...containerStyle
      }}
      className="bg-light d-flex flex-column bar-container bar-visible-file-popup"
    >
      <div
        className={`flex-grow-1 position-relative overflow-hidden d-flex justify-content-center ${
          canAnnotate ? '' : 'p-3 p-md-5'
        }`}
        style={{ minHeight: 0 }}
      >
        {!fileHref ? (
          <div className="align-self-center text-center">
            <CircularProgressBar size={4} />
            <h6 className="text-muted midFont mt-3">
              <i>Loading file ({humanFileSize(file.length)})...</i>
            </h6>
          </div>
        ) : (
          <FileRenderer
            fileType={file.contentType || (isPdf ? 'application/pdf' : '')}
            src={fileHref}
            highlights={highlights}
            onHighlightsChange={handleHighlightsChange}
            enableAnnotations={canAnnotate}
          />
        )}
      </div>

      {mode === REPORT_GENERATION_MODES.QUICK_PREVIEW && (
        <div
          className="flex-shrink-0"
          style={{
            padding: '12px 0',
            backgroundColor: 'var(--danger)',
            color: 'white'
          }}
        >
          <div className="d-flex align-items-center justify-content-center xlargeFont">
            <IoIosWarning className="mr-2 flex-shrink-0" size={24} color="var(--primary)" />{' '}
            <strong>You are in Quick Preview Mode. This PDF is not shareable</strong>
          </div>
        </div>
      )}

      <div
        className="bg-white w-100 px-2 py-1 flex-shrink-0"
        style={{
          boxShadow: '0 -2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              setFileHref();
              revokeBakedUrl();
              onHide();
            }}
          >
            <X size={18} />
            <span className="align-middle ml-2">Close</span>
          </Button>
          {fileHref && (
            <div>
              {canAnnotate && highlights.length > 0 && (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="border-dark-grey mr-2"
                  onClick={() => {
                    handleHighlightsChange([]);
                    revokeBakedUrl();
                    toast.success('Highlights cleared — original PDF preserved');
                  }}
                >
                  Clear Highlights
                </Button>
              )}
              {refreshFile && (
                <Button
                  size="sm"
                  variant="outline-success"
                  className="border-dark-grey mr-2"
                  disabled={isGeneratingReport || isBaking}
                  onClick={() => {
                    revokeBakedUrl();
                    refreshFile();
                  }}
                >
                  <ArrowClockwise size={18} />
                  <span className="align-middle ml-2">Refresh</span>
                </Button>
              )}
              {isDownloadable && (
                <Button
                  size="sm"
                  variant="dark"
                  className="border-dark-grey"
                  disabled={isBaking}
                  onClick={handleDownload}
                >
                  <Download size={18} />
                  <span className="align-middle ml-2">{isBaking ? 'Preparing...' : 'Download'}</span>
                </Button>
              )}
              {showQualiaShareButton &&
                onQualiaShareClick &&
                mode === REPORT_GENERATION_MODES.PREVIEW_AND_SHARE && (
                  <Button
                    size="sm"
                    variant="success"
                    className="border-dark-grey ml-2"
                    disabled={isBaking}
                    onClick={handleQualiaShare}
                  >
                    <img
                      src="/qualia-icon.png"
                      alt=""
                      width={16}
                      height={16}
                      className="mr-2"
                    />
                    <span className="align-middle">{isBaking ? 'Preparing...' : 'Sent Through Qualia'}</span>
                  </Button>
                )}
              {!showQualiaShareButton && onShareClick && mode === REPORT_GENERATION_MODES.PREVIEW_AND_SHARE && (
                <Button
                  size="sm"
                  variant="success"
                  className="border-dark-grey ml-2"
                  disabled={isBaking}
                  onClick={handleShare}
                >
                  <Share size={18} />
                  <span className="align-middle ml-2">{isBaking ? 'Preparing...' : 'Share'}</span>
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilePopup;
